// Per-key validation for the System Settings editor. The BE stores values as strings but
// *consumes* a subset as typed, bounded values (e.g. auth.lockoutMinutes drives the login
// lockout, clamped 1–1440 server-side). We mirror those bounds here so an admin physically
// cannot save a value the BE would reject or clamp — the input is constrained and Save is
// blocked until the draft is valid.

import type { SettingValueType } from './types';

export interface IntConstraint {
  min?: number;
  max?: number;
}

const INT_CONSTRAINTS: Record<string, IntConstraint> = {
  'auth.lockoutMinutes': { min: 1, max: 1440 },
  'auth.maxLoginAttempts': { min: 1, max: 100 },
  'auth.refreshTtlDays': { min: 1, max: 365 },
  'article.pageSize': { min: 1, max: 200 },
  'forum.postsPerPage': { min: 1, max: 200 },
  'featuring.maxItems': { min: 1, max: 50 },
  // Ceiling mirrors BE UploadConstraints.MaxConfigurableMb (request-size / multipart limits).
  'upload.maxFileSizeMb': { min: 1, max: 500 },
  'kvkk.deletionDelayDays': { min: 1, max: 3650 },
  'notifications.retentionDays': { min: 1, max: 3650 },
  'police.wsTimeoutSeconds': { min: 1, max: 600 },
};

/** Bounds for a known Int key; unknown Int keys default to a non-negative floor. */
export function intConstraint(key: string): IntConstraint {
  return INT_CONSTRAINTS[key] ?? { min: 0 };
}

export interface SettingError {
  /** i18n key under the `settings:validation.*` namespace. */
  key: string;
  values?: Record<string, number>;
}

/**
 * Validate a draft value against its declared type and per-key bounds.
 * Returns an i18n-ready error descriptor, or null when the value is acceptable.
 */
export function settingValueError(
  key: string,
  valueType: SettingValueType,
  value: string,
): SettingError | null {
  switch (valueType) {
    case 'Int': {
      const trimmed = value.trim();
      if (!/^-?\d+$/.test(trimmed)) return { key: 'validation.intRequired' };
      const n = Number(trimmed);
      const { min, max } = intConstraint(key);
      if (min != null && max != null && (n < min || n > max)) {
        return { key: 'validation.intRange', values: { min, max } };
      }
      if (min != null && n < min) return { key: 'validation.intMin', values: { min } };
      if (max != null && n > max) return { key: 'validation.intMax', values: { max } };
      return null;
    }
    case 'Json': {
      try {
        JSON.parse(value);
        return null;
      } catch {
        return { key: 'validation.json' };
      }
    }
    default:
      return null;
  }
}
